import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Subject, catchError, debounceTime, filter, of, switchMap } from 'rxjs';
import { environment } from 'app/environments/environment';
import { SearchResponse, SearchTabKey } from './global-search.types';

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private readonly _http = inject(HttpClient);
  private readonly _query$ = new Subject<string>();

  readonly query = signal<string>('');
  readonly activeTab = signal<SearchTabKey>('all');
  readonly results = signal<SearchResponse | null>(null);
  readonly isLoading = signal<boolean>(false);

  /** Derived: whether there is at least one result across all categories */
  readonly hasResults = computed(() => {
    const r = this.results();
    if (!r) return false;
    return r.categories.some(c => c.hits.length > 0);
  });

  constructor() {
    this._query$
      .pipe(
        debounceTime(300),
        filter(q => q.trim().length >= 2),
        switchMap(q => {
          this.isLoading.set(true);
          const params = new HttpParams()
            .set('q', q.trim())
            .set('limit', '5');
          return this._http
            .get<SearchResponse>(`${environment.apiURL}search`, { params })
            .pipe(catchError(() => of(null)));
        })
      )
      .subscribe(response => {
        this.results.set(response);
        this.isLoading.set(false);
      });
  }

  /**
   * Push a new query string. Triggers the debounced search pipeline.
   * Clears results immediately when query is too short.
   */
  search(query: string): void {
    this.query.set(query);
    if (query.trim().length < 2) {
      this.results.set(null);
      this.isLoading.set(false);
    }
    this._query$.next(query);
  }

  /** Reset to initial state (e.g. when the panel closes) */
  reset(): void {
    this.query.set('');
    this.activeTab.set('all');
    this.results.set(null);
    this.isLoading.set(false);
    this._query$.next('');
  }
}
